import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import { getContactIdFromUniqueId } from "../../contacts-search/helpers/common";
import type { RevealedContactDto } from "../../contacts-search/types/dto";
import type { CommonState } from "../types";

import * as actionCreators from "./action-creators";

const INITIAL_COMMON_STATE: CommonState = {
    workspaceId: null,
    workspacesFetching: true,
    websitesData: {},
    notFoundListModalOpen: false,
    notFoundLeadGenModalOpen: false,
    excelQuota: {
        total: 0,
        remaining: 0,
        used: 0,
    },
    isExcelQuotaLoading: false,
    multiSelectorPanel: {
        type: null,
        selectedMode: null,
        selectedItemIndex: 0,
    },
    notRevealedContactsIds: [],
    revealedContactsById: {},
    contactIdsBeingRevealed: [],
    isExtensionBannerVisible: false,
    isExtensionTopBannerVisible: undefined,
    isOnboardingWizardOpened: false,
    isColumnPickerOpen: false,
    activePageTab: null,
};

const commonReducer = createReducer<CommonState, ActionType<typeof actionCreators>>(INITIAL_COMMON_STATE)
    .handleAction(actionCreators.fetchLegacyWorkspaces.request, (state) => ({
        ...state,
        workspacesFetching: true,
    }))
    .handleAction(actionCreators.fetchLegacyWorkspaces.failure, (state) => ({
        ...state,
        workspacesFetching: false,
    }))
    .handleAction(
        actionCreators.fetchLegacyWorkspaces.success,
        (state): CommonState => ({
            ...state,
            workspacesFetching: false,
        }),
    )
    .handleAction(
        actionCreators.setWorkspaceId,
        (state, { payload }): CommonState => ({
            ...state,
            workspaceId: payload,
        }),
    )
    .handleAction(
        actionCreators.fetchSimilarWebsitesAsync.success,
        (state, { payload }): CommonState => ({
            ...state,
            websitesData: {
                ...state.websitesData,
                [payload.domain]: {
                    ...state.websitesData[payload.domain],
                    similarWebsites: payload.similarWebsites,
                },
            },
        }),
    )
    .handleAction(
        actionCreators.fetchWebsitesInfoAsync.success,
        (state, { payload }): CommonState => ({
            ...state,
            websitesData: {
                ...state.websitesData,
                [payload.domain]: {
                    ...state.websitesData[payload.domain],
                    info: payload.info,
                },
            },
        }),
    )
    .handleAction(
        actionCreators.toggleNotFoundListModalOpen,
        (state, { payload }): CommonState => ({
            ...state,
            notFoundListModalOpen: payload,
        }),
    )
    .handleAction(
        actionCreators.toggleNotFoundLeadGenModalOpen,
        (state, { payload }): CommonState => ({
            ...state,
            notFoundLeadGenModalOpen: payload,
        }),
    )
    .handleAction(
        actionCreators.fetchExcelQuota.request,
        (state): CommonState => ({
            ...state,
            isExcelQuotaLoading: true,
        }),
    )
    .handleAction(
        actionCreators.fetchExcelQuota.success,
        (state, { payload }): CommonState => ({
            ...state,
            isExcelQuotaLoading: false,
            excelQuota: {
                total: payload.total,
                remaining: payload.remaining,
                used: payload.used,
            },
        }),
    )
    .handleAction(
        actionCreators.fetchExcelQuota.failure,
        (state): CommonState => ({
            ...state,
            isExcelQuotaLoading: false,
        }),
    )
    .handleAction(
        actionCreators.setMultiSelectorPanel,
        (state, { payload }): CommonState => ({
            ...state,
            multiSelectorPanel: {
                type: payload.type,
                selectedMode: payload.selectedMode,
                selectedItemIndex: 0,
            },
        }),
    )
    .handleAction(
        actionCreators.updateMultiSelectorPanel,
        (state, { payload }): CommonState => ({
            ...state,
            multiSelectorPanel: {
                type: state.multiSelectorPanel.type,
                selectedMode: payload.selectedMode,
                selectedItemIndex: payload.selectedItemIndex,
            },
        }),
    )
    .handleAction(
        actionCreators.updateNotRevealedContactsIds,
        (state, { payload }): CommonState => ({
            ...state,
            notRevealedContactsIds: [...state.notRevealedContactsIds, ...payload],
        }),
    )
    .handleAction(
        actionCreators.addRevealingContactIds,
        (state, { payload }): CommonState => ({
            ...state,
            contactIdsBeingRevealed: [...state.contactIdsBeingRevealed, ...payload],
        }),
    )
    .handleAction(
        actionCreators.removeRevealingContactIds,
        (state, { payload }): CommonState => ({
            ...state,
            contactIdsBeingRevealed: state.contactIdsBeingRevealed.filter((id) => !payload.includes(id)),
        }),
    )
    .handleAction(actionCreators.updateRevealedContactsByUniqId, (state, { payload }): CommonState => {
        return {
            ...state,
            revealedContactsById: payload.reduce(
                (acc: Record<string, RevealedContactDto>, contact: RevealedContactDto) => {
                    acc[getContactIdFromUniqueId(contact.id)] = contact;
                    return acc;
                },
                { ...state.revealedContactsById },
            ),
        };
    })
    .handleAction(actionCreators.updateRevealedContactsById, (state, { payload }): CommonState => {
        return {
            ...state,
            revealedContactsById: payload.reduce(
                (acc: Record<string, RevealedContactDto>, contact: RevealedContactDto) => {
                    acc[contact.id] = contact;
                    return acc;
                },
                { ...state.revealedContactsById },
            ),
        };
    })
    .handleAction(
        actionCreators.setIsOnboardingWizardOpened,
        (state, { payload }): CommonState => ({
            ...state,
            isOnboardingWizardOpened: payload,
        }),
    )
    .handleAction(
        actionCreators.setIsExtensionBannerVisible,
        (state, { payload }): CommonState => ({
            ...state,
            isExtensionBannerVisible: payload,
        }),
    )
    .handleAction(
        actionCreators.setIsColumnPickerOpen,
        (state, { payload }): CommonState => ({
            ...state,
            isColumnPickerOpen: payload,
        }),
    )
    .handleAction(
        actionCreators.setActivePageTab,
        (state, { payload }): CommonState => ({
            ...state,
            activePageTab: payload,
        }),
    );
export default commonReducer;
